import type { MaybeRefOrGetter } from '@vueuse/core';
import { createGlobalState, createInjectionState } from '@vueuse/core';
import { toValue } from 'vue';

export function createSimpleInjectionState<Return>() {
    type State = MaybeRefOrGetter<Return | undefined> | Return;
    const [_useProvideSimpleInjectionState, _useSimpleInjectionState] = createInjectionState(() => {
        let state: State | undefined;
        const setState = (_state: State) => {
            state = _state;
        };
        const getState = () => state;
        return { setState, getState };
    });

    function _useProvideSimpleInjectionHandler(_state0?: State) {
        const { setState, getState } = _useProvideSimpleInjectionState();
        const setter = (_state: State) => {
            if(getState() !== undefined) {
                throw new Error('Store is already provided');
            }
            setState(_state);
        };
        if(_state0 !== undefined) {
            setter(_state0);
        }
        return setter;
    }

    function useSimpleInjectionHandler() {
        const handler = _useSimpleInjectionState();
        if(!handler) {
            throw new Error('Store is not provided');
        }
        const getter = (): Return => {
            const value = toValue(handler.getState());
            if(value === undefined) {
                throw new Error('Store is not provided');
            }
            return value;
        };
        return getter;
    }

    return [_useProvideSimpleInjectionHandler, useSimpleInjectionHandler] as const;
}


export function createSimpleGlobalStateProvider<Return>() {
    type State = MaybeRefOrGetter<Return | undefined> | Return;
    const useGlobalState = createGlobalState(() => {
        let state: State | undefined;
        const setState = (_state: State) => {
            state = _state;
        };
        const getState = () => state;
        return { setState, getState };
    });

    function useProvideSimpleGlobalState(_state: State) {
        const { setState, getState } = useGlobalState();
        if(getState() !== undefined) {
            throw new Error('Store is already provided');
        }
        setState(_state);
    }

    const useSimpleGlobalState = (): Return => {
        const { getState } = useGlobalState();
        const _state = toValue(getState());
        if(_state === undefined) {
            throw new Error('Store is not provided');
        }
        return _state;
    };

    return [useProvideSimpleGlobalState, useSimpleGlobalState] as const;
}
